import { useCallback, useEffect, useRef } from 'react';

import type { Subscription } from '@rails/actioncable';

import type { CableEventDataType } from '~/interfaces/cable';
import { createCommunityConsumer } from '~/services/cable';

// Refresh presence well within the backend's 60s TTL (PRESENCE_TTL)
const HEARTBEAT_INTERVAL_MS = 15000;

export type CableEventHandlers = {
  [E in CableEventDataType as E['type']]?: (event: E) => unknown;
};

type PerformFn = (action: string, data?: object) => void;

interface UseCommunityCableSubscriptionParams {
  isMember: boolean;
  eventHandlers: CableEventHandlers;
  onRejected?: () => void;
}

function usePresenceHeartbeat(enabled: boolean, perform: PerformFn) {
  useEffect(() => {
    if (!enabled) return;

    // A hidden tab must not keep the member online: last_seen_at decides which notification emails send.
    const sendHeartbeatWhenVisible = () => {
      if (document.visibilityState === 'visible') perform('heartbeat');
    };

    const heartbeatInterval = setInterval(sendHeartbeatWhenVisible, HEARTBEAT_INTERVAL_MS);
    document.addEventListener('visibilitychange', sendHeartbeatWhenVisible);

    return () => {
      document.removeEventListener('visibilitychange', sendHeartbeatWhenVisible);
      clearInterval(heartbeatInterval);
    };
  }, [enabled, perform]);
}

export function useCommunityCableSubscription({
  isMember,
  eventHandlers,
  onRejected,
}: UseCommunityCableSubscriptionParams) {
  const channelRef = useRef<Subscription | null>(null);

  const handlersRef = useRef(eventHandlers);
  // avoid stale closure
  handlersRef.current = eventHandlers;

  const onRejectedRef = useRef(onRejected);
  onRejectedRef.current = onRejected;

  useEffect(() => {
    if (!isMember) return;

    const consumer = createCommunityConsumer();
    if (!consumer) return;

    channelRef.current = consumer.subscriptions.create('OrchidCable::CommunityChannel', {
      received(data) {
        const event = data as CableEventDataType;
        (handlersRef.current[event.type] as ((e: CableEventDataType) => unknown) | undefined)?.(event);
      },
      rejected() {
        onRejectedRef.current?.();
      },
    });

    return () => {
      channelRef.current?.unsubscribe();
      channelRef.current = null;
      consumer.disconnect();
    };
  }, [isMember]);

  const perform = useCallback((action: string, data?: object) => {
    channelRef.current?.perform(action, data);
  }, []);

  usePresenceHeartbeat(isMember, perform);

  return { perform };
}
